import React, { useState, useEffect, useRef, useCallback } from 'react';
import styled from 'styled-components';
import { Session, CommunicationChannel } from '../../models/session';
import { Message } from '../../models/message';
import ChatMessage from './ChatMessage';
import { getSlaInfo } from './SessionSlaIndicator';
// Import real API functions from chatApi.ts
import { getMessages, pauseAi, resumeAi, sendAdminMessage as sendAdminMessageApi, markSessionAsViewed, translateMessage } from '../../api/chatApi'; 
import { getAiProcessingLogs } from '../../api/aiProcessingLogApi';
import logger from '../../utils/logger'; // Assuming logger is available
import socketService, {
    type MessageDeliveryUpdate,
    type SessionDetailUpdate,
} from "../../services/socketService";
import {
    decideSessionAuthorityUpdate,
    hasValidSessionAuthoritySnapshot,
    isValidSessionHttpSnapshot,
    readSessionAuthorityVersion,
} from '../../services/sessionAuthority';
import { IconButton, useTheme, Tooltip, CircularProgress } from '@mui/material'; // Added IconButton and useTheme
import { useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack'; // Added ArrowBackIcon
import TranslateIcon from '@mui/icons-material/Translate'; // Translation icon
import type {
    AiProcessingLogSummary,
    AiProcessingLogUpdate,
} from '../../models/aiProcessingLog';
import SuggestAttributionDialog from './SuggestAttributionDialog';
import { v4 as uuidv4 } from 'uuid';
import { useConversationLifecycle } from '../../features/conversationLifecycle/useConversationLifecycle';
import {
    applyMessageDeliveryUpdate,
    isTerminalDeliveryState,
    mergeConversationMessage,
    messageClientRequestId,
    reconcileConversationMessages,
} from './messageReconciliation';
import { useFeatureFlag } from '../../context/FeatureFlagsContext';
import {
    type AdminSendAttempt,
    isAdminSendDeliveryUpdateForMessage,
    isAdminSendDeliveryUpdateForAttempt,
    isAdminSendReceiptMessageForAttempt,
    isAmbiguousAdminSendFailure,
} from './adminSendAttempt';
import { lifecycleStatusText } from '../../features/conversationLifecycle/presentation';

const diagnosticErrorType = (error: unknown): string => {
    const name = error instanceof Error ? error.name : '';
    return /^[A-Za-z][A-Za-z0-9]{0,63}$/.test(name) ? name : 'Error';
};

const reconcileAiProcessingLogs = (
    current: AiProcessingLogSummary[],
    incoming: AiProcessingLogSummary[],
): AiProcessingLogSummary[] => {
    const byId = new Map(current.map((log) => [log.id, log]));
    incoming.forEach((log) => byId.set(log.id, log));
    return Array.from(byId.values())
        .sort((left, right) =>
            right.createdAt.localeCompare(left.createdAt) ||
            right.id.localeCompare(left.id),
        )
        .slice(0, 200);
};

interface ChatConversationProps {
    session: Session;
    onBack?: () => void; // Added onBack prop
    isMobile?: boolean;  // Added isMobile prop
    embedded?: boolean;
    sessionStateSynchronized?: boolean;
}

type SessionAuthorityState = Pick<
    Session,
    | 'id'
    | 'workerId'
    | 'aiPaused'
    | 'adminIntervention'
    | 'currentQuestionId'
    | 'currentQuestionType'
    | 'stateVersion'
    | 'takeoverEpoch'
>;

const ConversationContainer = styled.div<{ $isMobile: boolean; $isDarkMode: boolean; $embedded: boolean }>`
    flex: 1;
    display: flex;
    flex-direction: column;
    background-color: ${props => props.$isDarkMode ? '#0B0F1A' : '#F8FAFC'};
    overflow: hidden;
    min-height: 0;
    height: ${props => props.$embedded ? '100%' : props.$isMobile ? '100dvh' : 'auto'};
`;

const Header = styled.div<{ $isDarkMode: boolean }>`
    padding: 10px 20px;
    background-color: ${props => props.$isDarkMode ? '#111827' : '#FFFFFF'};
    border-bottom: 1px solid ${props => props.$isDarkMode ? '#1E293B' : '#E2E8F0'};
    display: flex;
    justify-content: space-between;
    align-items: center;
    min-height: 59px; 

    @media (max-width: 768px) {
        flex-direction: column;
        align-items: flex-start;
        padding: 10px;
    }
`;

const HeaderContent = styled.div`
    display: flex;
    align-items: center;
    flex-grow: 1;
`;

const WorkerInfo = styled.div<{ $isDarkMode: boolean }>`
    font-weight: 500;
    color: ${props => props.$isDarkMode ? '#F1F5F9' : '#0F172A'};
    @media (max-width: 768px) {
        margin-bottom: 5px;
    }
`;

const StatusIndicator = styled.span<{$isAdminIntervention: boolean; $isDarkMode: boolean}>`
    font-size: 0.8rem;
    color: ${props => props.$isAdminIntervention ? (props.$isDarkMode ? '#ff6b6b' : '#d9534f') : (props.$isDarkMode ? '#999' : '#667781')}; 
    margin-left: 10px;
`;

const ActionButtons = styled.div`
    display: flex;
    gap: 10px;
    @media (max-width: 768px) {
        width: 100%;
        justify-content: space-around;
    }
`;

const Button = styled.button`
    padding: 8px 15px;
    border: none;
    border-radius: 5px;
    cursor: pointer;
    font-size: 0.9rem;
    background-color: #2563EB;
    color: white;
    transition: background-color 0.2s ease;

    @media (max-width: 768px) {
        flex-grow: 1;
        font-size: 0.8rem;
        padding: 10px 5px;
    }

    &:hover {
        background-color: #1D4ED8;
    }

    &:disabled {
        background-color: #cccccc;
        cursor: not-allowed;
    }
`;

const MessageList = styled.div`
    flex: 1;
    padding: 20px;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    gap: 10px;
`;

const MessageInputContainer = styled.div<{ $isDarkMode: boolean }>`
    padding: 10px 20px;
    background-color: ${props => props.$isDarkMode ? '#111827' : '#FFFFFF'};
    border-top: 1px solid ${props => props.$isDarkMode ? '#1E293B' : '#E2E8F0'};
    display: flex;
    align-items: center;

    @media (max-width: 768px) {
        padding: 10px;
    }
`;

const ExpiredNotice = styled.div<{ $isDarkMode: boolean }>`
    padding: 8px 20px;
    font-size: 0.8rem;
    line-height: 1.4;
    color: ${props => props.$isDarkMode ? '#FCD34D' : '#92400E'};
    background-color: ${props => props.$isDarkMode ? '#78350F' : '#FEF3C7'};
    border-top: 1px solid ${props => props.$isDarkMode ? '#1E293B' : '#E2E8F0'};

    @media (max-width: 768px) {
        padding: 8px 10px;
    }
`;

const Input = styled.input<{ $isDarkMode: boolean }>`
    flex: 1;
    padding: 10px 15px;
    border: 1px solid ${props => props.$isDarkMode ? '#1E293B' : '#E2E8F0'};
    border-radius: 20px;
    margin-right: 10px;
    font-size: 1rem;
    background-color: ${props => props.$isDarkMode ? '#1E293B' : '#F8FAFC'};
    color: ${props => props.$isDarkMode ? '#F1F5F9' : '#0F172A'};

    @media (max-width: 768px) {
        font-size: 0.9rem;
        padding: 8px 12px;
    }

    &:disabled {
        background-color: ${props => props.$isDarkMode ? '#111827' : '#F1F5F9'};
    }
`;

const SendButton = styled(Button)`
    background-color: #00a884; 
    &:hover {
        background-color: #007a5f;
    }
`;

const NoChatSelected = styled.div<{ $isDarkMode: boolean }>`
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.2rem;
    color: ${props => props.$isDarkMode ? '#94A3B8' : '#64748B'};
    background-color: ${props => props.$isDarkMode ? '#111827' : '#F8FAFC'};
`;

const TabSwitcher = styled.div<{ $isDarkMode: boolean }>`
    display: flex;
    gap: 8px;
    padding: 8px 12px;
    border-bottom: 1px solid ${props => props.$isDarkMode ? '#1E293B' : '#E2E8F0'};
    background-color: ${props => props.$isDarkMode ? '#0B0F1A' : '#F8FAFC'};
`;

const TabButton = styled.button<{ $active: boolean; $isDarkMode: boolean }>`
    border: none;
    border-radius: 18px;
    padding: 8px 14px;
    cursor: pointer;
    font-weight: 600;
    background-color: ${props => props.$active ? (props.$isDarkMode ? '#60A5FA' : '#2563EB') : 'transparent'};
    color: ${props => props.$active ? '#fff' : (props.$isDarkMode ? '#94A3B8' : '#64748B')};
    border: 1px solid ${props => props.$active ? 'transparent' : (props.$isDarkMode ? '#1E293B' : '#E2E8F0')};
    transition: background-color 0.15s ease, color 0.15s ease;
`;

const DebugPanel = styled.div<{ $isDarkMode: boolean }>`
    flex: 1;
    padding: 12px 16px;
    overflow-y: auto;
    background: ${props => props.$isDarkMode ? '#0B0F1A' : '#F8FAFC'};
`;

const LogCard = styled.div<{ $isDarkMode: boolean }>`
    border: 1px solid ${props => props.$isDarkMode ? '#1E293B' : '#E2E8F0'};
    background: ${props => props.$isDarkMode ? '#111827' : '#FFFFFF'};
    border-radius: 10px;
    padding: 12px;
    margin-bottom: 12px;
    box-shadow: ${props => props.$isDarkMode ? 'none' : '0 1px 2px rgba(0,0,0,0.08)'};
`;

const LogHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
`;

const LogBadges = styled.div`
    display: flex;
    gap: 6px;
    align-items: center;
    flex-wrap: wrap;
`;

const Badge = styled.span<{ $tone: 'in' | 'out' | 'warn' | 'info' }>`
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 4px 8px;
    border-radius: 12px;
    font-size: 0.8rem;
    background-color: ${props => {
        switch (props.$tone) {
            case 'in': return '#e8f5e9';
            case 'out': return '#e3f2fd';
            case 'warn': return '#fff3cd';
            default: return '#eceff1';
        }
    }};
    color: #1f1f1f;
`;

const DebugMeta = styled.div<{ $isDarkMode: boolean }>`
    margin: 6px 0;
    color: ${props => props.$isDarkMode ? '#94A3B8' : '#64748B'};
    font-size: 0.9rem;
`;

const ChatConversation: React.FC<ChatConversationProps> = ({ session, onBack, isMobile, embedded = false, sessionStateSynchronized = true }) => {
    const theme = useTheme();
    const navigate = useNavigate();
    const isDarkMode = theme.palette.mode === 'dark';
    const [messages, setMessages] = useState<Message[]>([]);
    const [isLoadingMessages, setIsLoadingMessages] = useState<boolean>(false);
    const [aiLogs, setAiLogs] = useState<AiProcessingLogSummary[]>([]);
    const [isLoadingAiLogs, setIsLoadingAiLogs] = useState<boolean>(false);
    const [activeTab, setActiveTab] = useState<'conversation' | 'debug'>('conversation');
    const [adminMessage, setAdminMessage] = useState("");
    const renderedSessionSnapshotValid = isValidSessionHttpSnapshot(session);
    const initialAuthoritySnapshotValid = renderedSessionSnapshotValid;
    const [isAiPaused, setIsAiPaused] = useState<boolean>(
        initialAuthoritySnapshotValid ? session.aiPaused : false,
    );
    const [isAdminIntervened, setIsAdminIntervened] = useState<boolean>(
        initialAuthoritySnapshotValid ? session.adminIntervention : false,
    );
    const [authoritySnapshotValid, setAuthoritySnapshotValid] = useState(
        initialAuthoritySnapshotValid,
    );
    const [showTranslations, setShowTranslations] = useState<boolean>(false);
    const [isTranslating, setIsTranslating] = useState<boolean>(false);
    const [suggestDialogOpen, setSuggestDialogOpen] = useState<boolean>(false);
    const [adminSendPending, setAdminSendPending] = useState<boolean>(false);
    const [ambiguousAdminSend, setAmbiguousAdminSend] = useState<AdminSendAttempt | null>(null);
    const lifecycleEventsEnabled = useFeatureFlag('conversationRoutingV2');
    const { state: lifecycleState, latest: latestLifecycle } = useConversationLifecycle(
        String(session.id),
        session.workerId,
        lifecycleEventsEnabled && renderedSessionSnapshotValid && sessionStateSynchronized,
    );
    const lifecycleStatus = lifecycleStatusText(latestLifecycle);
    // Show "Suggest attribution" only when this session has no referral link
    // attached yet — once it's attributed (any tier), the chip in the sidebar
    // tells the story and the operator doesn't need to re-pick.
    const needsAttribution = !session.referralLinkId;
    const messagesEndRef = useRef<HTMLDivElement>(null);
    const currentWorkerIdRef = useRef<string | null>(null);
    const diagnosticLoadGenerationRef = useRef(0);
    const authoritativeAdminRequestIdsRef = useRef<Set<string>>(new Set());
    const renderedSessionIdentityRef = useRef({ id: session.id, workerId: session.workerId });
    const sessionGenerationRef = useRef(1);
    const adminSendGenerationRef = useRef(0);
    if (
        renderedSessionIdentityRef.current.id !== session.id ||
        renderedSessionIdentityRef.current.workerId !== session.workerId
    ) {
        renderedSessionIdentityRef.current = { id: session.id, workerId: session.workerId };
        sessionGenerationRef.current += 1;
        adminSendGenerationRef.current += 1;
    }
    const activeAdminSendRef = useRef<AdminSendAttempt | null>(null);
    const ambiguousAdminSendRef = useRef<AdminSendAttempt | null>(null);
    const messageReconciliationRef = useRef<(() => void) | null>(null);
    const initialAuthorityVersion = readSessionAuthorityVersion(session);
    const sessionAuthorityRef = useRef<SessionAuthorityState | null>(
        initialAuthoritySnapshotValid && initialAuthorityVersion
            ? {
                id: session.id,
                workerId: session.workerId,
                aiPaused: session.aiPaused,
                adminIntervention: session.adminIntervention,
                currentQuestionId: session.currentQuestionId,
                currentQuestionType: session.currentQuestionType,
                ...initialAuthorityVersion,
            }
            : null,
    );

    const applySessionControlUpdate = useCallback((update: Partial<Session>): boolean => {
        const current = sessionAuthorityRef.current;
        if (
            !current ||
            update.workerId !== current.workerId ||
            (update.id !== undefined && update.id !== current.id)
        ) return false;
        if (decideSessionAuthorityUpdate(current, update) !== 'APPLY') {
            logger.warn('Socket: stale or malformed session authority update ignored');
            return false;
        }
        const incomingVersion = readSessionAuthorityVersion(update);
        if (!incomingVersion) return false;
        sessionAuthorityRef.current = {
            ...current,
            ...(typeof update.aiPaused === 'boolean' ? { aiPaused: update.aiPaused } : {}),
            ...(typeof update.adminIntervention === 'boolean'
                ? { adminIntervention: update.adminIntervention }
                : {}),
            ...(update.currentQuestionId !== undefined
                ? { currentQuestionId: update.currentQuestionId }
                : {}),
            ...(update.currentQuestionType !== undefined
                ? { currentQuestionType: update.currentQuestionType }
                : {}),
            ...incomingVersion,
        };
        setAuthoritySnapshotValid(true);
        if (typeof update.aiPaused === 'boolean') setIsAiPaused(update.aiPaused);
        if (typeof update.adminIntervention === 'boolean') {
            setIsAdminIntervened(update.adminIntervention);
        }
        return true;
    }, []);

    // Helper function to get display name for a session
    const getSessionDisplayName = (session: Session): string => {
        if (session.workerFirstName && session.workerLastName) {
            return `${session.workerFirstName} ${session.workerLastName}`;
        } else if (session.workerFirstName) {
            return session.workerFirstName;
        } else if (session.workerLastName) {
            return session.workerLastName;
        } else if (session.phoneNumber) {
            return session.phoneNumber;
        } else {
            return session.workerId;
        }
    }; 
    const formatTimestamp = (timestamp: string) => {
        try {
            return new Date(timestamp).toLocaleTimeString();
        } catch {
            return timestamp;
        }
    };
    const currentSessionRef = useRef<Session | null>(null);

    useEffect(() => {
        ambiguousAdminSendRef.current = ambiguousAdminSend;
    }, [ambiguousAdminSend]);

    // Update currentSessionRef whenever session prop changes
    useEffect(() => {
        currentSessionRef.current = session;
    }, [session]);

    const scrollToBottom = () => {
        // messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
        // Ensure instant scroll to bottom
        if (messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView(); // Default behavior is instant
        }
    };

    // Stabilize handleNewMessage with useCallback
    const handleNewMessage = useCallback((newMessage: Message) => {
        const activeIdentity = renderedSessionIdentityRef.current;
        if (
            newMessage.workerId === activeIdentity.workerId &&
            String(newMessage.sessionId) === String(activeIdentity.id)
        ) {
            logger.debug('Socket: new message received for active session');
            const activeAttempt = activeAdminSendRef.current ?? ambiguousAdminSendRef.current;
            const expectedLogicalSendKey = activeAttempt
                ? `admin:${activeAttempt.sessionId}:${activeAttempt.clientRequestId}`
                : null;
            const claimsActiveAttempt = Boolean(
                activeAttempt &&
                (
                    newMessage.clientRequestId === activeAttempt.clientRequestId ||
                    newMessage.logicalSendKey === expectedLogicalSendKey
                ),
            );
            if (
                activeAttempt &&
                claimsActiveAttempt &&
                !isAdminSendReceiptMessageForAttempt(activeAttempt, newMessage)
            ) {
                logger.warn('Socket: conflicting admin receipt ignored; reconciling authoritative messages');
                messageReconciliationRef.current?.();
                return;
            }
            if (
                activeAttempt &&
                isAdminSendReceiptMessageForAttempt(activeAttempt, newMessage)
            ) {
                authoritativeAdminRequestIdsRef.current.add(activeAttempt.clientRequestId);
            }
            setMessages(prevMessages => mergeConversationMessage(prevMessages, newMessage));
            setAmbiguousAdminSend((current) => current && isAdminSendReceiptMessageForAttempt(current, newMessage)
                ? null
                : current);

            // If this message is for the currently viewed session, mark it as viewed
            if (currentSessionRef.current && newMessage.workerId === currentSessionRef.current.workerId) {
                logger.info('ChatConversation: marking active conversation as viewed');
                markSessionAsViewed(newMessage.workerId)
                    .then(() => logger.debug('ChatConversation: active conversation marked as viewed'))
                    .catch((error) => logger.error(
                        `ChatConversation: mark-viewed failed errorType=${diagnosticErrorType(error)}`,
                    ));
            }

        } else {
            logger.debug('Socket: message for another session ignored');
        }
    }, []); // Empty dependency array means this callback is created once and never changes.

    const handleDeliveryUpdate = useCallback((update: MessageDeliveryUpdate) => {
        if (update.workerId !== currentWorkerIdRef.current) return;
        setMessages((current) => current.map((message) => {
            if (
                message.direction === 'OUTGOING' &&
                message.senderType === 'ADMIN' &&
                !isAdminSendDeliveryUpdateForMessage(message, update)
            ) return message;
            return applyMessageDeliveryUpdate([message], update)[0];
        }));
        if (isTerminalDeliveryState(update.status)) {
            const activeAttempt = activeAdminSendRef.current;
            if (
                activeAttempt &&
                isAdminSendDeliveryUpdateForAttempt(activeAttempt, update)
            ) {
                authoritativeAdminRequestIdsRef.current.add(activeAttempt.clientRequestId);
            }
            setAmbiguousAdminSend((current) => current &&
                isAdminSendDeliveryUpdateForAttempt(current, update)
                ? null
                : current);
        }
    }, []);

    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    // Reconcile over HTTP on mount and every reconnect; socket events only bridge
    // the interval between authoritative snapshots.
    useEffect(() => {
        const workerId = session?.workerId;
        const sessionId = session?.id;

        if (workerId && sessionId) {
            currentWorkerIdRef.current = workerId;
            authoritativeAdminRequestIdsRef.current.clear();
            activeAdminSendRef.current = null;
            ambiguousAdminSendRef.current = null;
            setAdminSendPending(false);
            setAmbiguousAdminSend(null);
            setAdminMessage('');
            setMessages([]);
            setIsLoadingMessages(true);

            const socket = socketService.connect();
            let active = true;
            let reconciliationInFlight = false;
            let reconciliationQueued = false;
            const isCurrentSession = () =>
                active &&
                currentWorkerIdRef.current === workerId &&
                renderedSessionIdentityRef.current.id === sessionId;
            const reconcileMessages = async () => {
                if (!isCurrentSession()) return;
                if (reconciliationInFlight) {
                    reconciliationQueued = true;
                    return;
                }
                reconciliationInFlight = true;
                try {
                    const fetchedMessages = (await getMessages(workerId)).filter(
                        (message) => String(message.sessionId) === String(sessionId),
                    );
                    if (!isCurrentSession()) return;
                    fetchedMessages.forEach((message) => {
                        const activeAttempt = activeAdminSendRef.current;
                        if (
                            activeAttempt &&
                            isAdminSendReceiptMessageForAttempt(activeAttempt, message)
                        ) {
                            authoritativeAdminRequestIdsRef.current.add(activeAttempt.clientRequestId);
                        }
                    });
                    setMessages((current) => reconcileConversationMessages(current, fetchedMessages));
                    setAmbiguousAdminSend((current) => current && fetchedMessages.some(
                        (message) => isAdminSendReceiptMessageForAttempt(current, message),
                    ) ? null : current);
                    logger.info(`ChatConversation: reconciled messageCount=${fetchedMessages.length}`);
                } catch (error) {
                    if (isCurrentSession()) logger.error(
                        `ChatConversation: message reconciliation failed errorType=${diagnosticErrorType(error)}`,
                    );
                } finally {
                    reconciliationInFlight = false;
                    if (!isCurrentSession()) {
                        reconciliationQueued = false;
                    } else if (reconciliationQueued) {
                        reconciliationQueued = false;
                        void reconcileMessages();
                    } else {
                        setIsLoadingMessages(false);
                    }
                }
            };
            const handleConnect = () => {
                void reconcileMessages();
            };
            const requestMessageReconciliation = () => {
                void reconcileMessages();
            };
            messageReconciliationRef.current = requestMessageReconciliation;

            socketService.onNewMessage(handleNewMessage);
            socketService.onMessageDeliveryUpdate(handleDeliveryUpdate);
            socket.on('connect', handleConnect);
            void reconcileMessages();

            return () => {
                logger.info('ChatConversation: cleaning up room listeners');
                active = false;
                if (messageReconciliationRef.current === requestMessageReconciliation) {
                    messageReconciliationRef.current = null;
                }
                socket.off('connect', handleConnect);
                socketService.offNewMessage(handleNewMessage); // Pass the specific handler
                socketService.offMessageDeliveryUpdate(handleDeliveryUpdate);
            };
        } else {
            setMessages([]);
            currentWorkerIdRef.current = null;
            authoritativeAdminRequestIdsRef.current.clear();
            activeAdminSendRef.current = null;
            setAdminSendPending(false);
        }
        setAmbiguousAdminSend(null);
        setAdminMessage('');
    }, [session?.id, session?.workerId, handleNewMessage, handleDeliveryUpdate]);

    // Debug diagnostics are loaded only when explicitly opened.
    useEffect(() => {
        const workerId = session?.workerId;
        const generation = ++diagnosticLoadGenerationRef.current;
        let active = true;
        const isCurrentDiagnosticView = () =>
            active &&
            generation === diagnosticLoadGenerationRef.current &&
            currentWorkerIdRef.current === workerId;

        if (!workerId || activeTab !== 'debug') {
            setAiLogs([]);
            setIsLoadingAiLogs(false);
            return () => {
                active = false;
            };
        }

        setAiLogs([]);
        setIsLoadingAiLogs(true);

        // `aiLogUpdate` is intentionally identity-free. Authorization and room
        // ownership are enforced server-side; this active worker/generation
        // fence prevents a late event from a room being left during a switch.
        const handleRoomAiLogUpdate = (log: AiProcessingLogUpdate) => {
            if (!isCurrentDiagnosticView()) {
                logger.debug('Socket: stale AI diagnostic ignored');
                return;
            }
            setAiLogs((current) => reconcileAiProcessingLogs(current, [log]));
        };
        socketService.onAiLogUpdate(handleRoomAiLogUpdate);

        void getAiProcessingLogs(workerId, 150)
            .then(fetchedLogs => {
                if (!isCurrentDiagnosticView()) return;
                setAiLogs((current) =>
                    reconcileAiProcessingLogs(current, fetchedLogs),
                );
                logger.info(`ChatConversation: loaded diagnosticCount=${fetchedLogs.length}`);
            })
            .catch(error => {
                if (!isCurrentDiagnosticView()) return;
                logger.error(
                    `ChatConversation: diagnostic fetch failed errorType=${diagnosticErrorType(error)}`,
                );
            })
            .finally(() => {
                if (isCurrentDiagnosticView()) setIsLoadingAiLogs(false);
            });

        return () => {
            active = false;
            socketService.offAiLogUpdate(handleRoomAiLogUpdate);
        };
    }, [session?.workerId, activeTab]);

    // Effect for handling session detail updates (AI paused, admin intervention)
    useEffect(() => {
        const workerId = session?.workerId;

        if (workerId) {
            const handleSessionDetailEvent = (detailUpdate: SessionDetailUpdate) => {
                if (detailUpdate.workerId === workerId) { // Ensure update is for the correct session
                    logger.debug('Socket: session detail update received in ChatConversation');
                    applySessionControlUpdate(detailUpdate);
                }
            };
            socketService.onSessionDetailUpdate(handleSessionDetailEvent);

            return () => {
                // No need to log leaving room here as the main effect handles it.
                socketService.offSessionDetailUpdate(handleSessionDetailEvent); // Pass the specific handler
            };
        }
    }, [applySessionControlUpdate, session?.workerId]); // Re-subscribe if workerId changes

    // Effect to sync local AI/Admin status from the session prop if it changes
    useEffect(() => {
        if (!isValidSessionHttpSnapshot(session)) {
            sessionAuthorityRef.current = null;
            setIsAiPaused(false);
            setIsAdminIntervened(false);
            setAuthoritySnapshotValid(false);
            logger.warn('ChatConversation: malformed session HTTP snapshot ignored');
            return;
        }
        const authoritySnapshot: Partial<Session> = {
            id: session.id,
            workerId: session.workerId,
            aiPaused: session.aiPaused,
            adminIntervention: session.adminIntervention,
            currentQuestionId: session.currentQuestionId,
            currentQuestionType: session.currentQuestionType,
            stateVersion: session.stateVersion,
            takeoverEpoch: session.takeoverEpoch,
        };
        const incomingVersion = readSessionAuthorityVersion(authoritySnapshot);
        if (!incomingVersion || !hasValidSessionAuthoritySnapshot(authoritySnapshot)) {
            setAuthoritySnapshotValid(false);
            logger.warn('ChatConversation: malformed session authority snapshot ignored');
            return;
        }
        if (
            sessionAuthorityRef.current?.workerId !== session.workerId ||
            sessionAuthorityRef.current.id !== session.id
        ) {
            sessionAuthorityRef.current = {
                id: session.id,
                workerId: session.workerId,
                aiPaused: session.aiPaused,
                adminIntervention: session.adminIntervention,
                currentQuestionId: session.currentQuestionId,
                currentQuestionType: session.currentQuestionType,
                ...incomingVersion,
            };
            setIsAiPaused(session.aiPaused);
            setIsAdminIntervened(session.adminIntervention);
            setAuthoritySnapshotValid(true);
            return;
        }
        applySessionControlUpdate(authoritySnapshot);
    }, [applySessionControlUpdate, session]);

    const sessionControlSynchronized = sessionStateSynchronized &&
        renderedSessionSnapshotValid &&
        authoritySnapshotValid &&
        sessionAuthorityRef.current?.id === session.id &&
        sessionAuthorityRef.current?.workerId === session.workerId;

    const handlePauseAi = async () => {
        if (!session || !sessionControlSynchronized) return;
        const sessionGeneration = sessionGenerationRef.current;
        const sessionId = session.id;
        const workerId = session.workerId;
        try {
            const updated = await pauseAi(workerId);
            if (
                sessionGeneration !== sessionGenerationRef.current ||
                sessionId !== renderedSessionIdentityRef.current.id ||
                workerId !== renderedSessionIdentityRef.current.workerId
            ) return;
            applySessionControlUpdate(updated);
            logger.info('ChatConversation: pause AI requested');
        } catch (error) {
            logger.error(
                `ChatConversation: pause AI failed errorType=${diagnosticErrorType(error)}`,
            );
        }
    };

    const handleResumeAi = async () => {
        if (!session || !sessionControlSynchronized) return;
        const sessionGeneration = sessionGenerationRef.current;
        const sessionId = session.id;
        const workerId = session.workerId;
        try {
            const updated = await resumeAi(workerId);
            if (
                sessionGeneration !== sessionGenerationRef.current ||
                sessionId !== renderedSessionIdentityRef.current.id ||
                workerId !== renderedSessionIdentityRef.current.workerId
            ) return;
            applySessionControlUpdate(updated);
            logger.info('ChatConversation: resume AI requested');
        } catch (error) {
            logger.error(
                `ChatConversation: resume AI failed errorType=${diagnosticErrorType(error)}`,
            );
        }
    };

    // The 24h WhatsApp window has lapsed for this conversation. While expired we
    // can't send a free-form message (only templates, not built yet), so the admin
    // composer is blocked until the worker replies. Only meaningful for WhatsApp.
    const slaExpired = session?.channel === CommunicationChannel.WHATSAPP
        && getSlaInfo(session?.lastInboundAt).expired;

    const handleSendAdminMessage = async () => {
        if (
            !session ||
            !sessionControlSynchronized ||
            activeAdminSendRef.current ||
            (!adminMessage.trim() && !ambiguousAdminSend)
        ) return;
        if (slaExpired) {
            logger.warn('ChatConversation: admin send blocked because the 24h window expired');
            return;
        }
        const sendGeneration = ++adminSendGenerationRef.current;
        const attempt: AdminSendAttempt = {
            ...(ambiguousAdminSend ?? {
                message: adminMessage.trim(),
                clientRequestId: uuidv4(),
                workerId: session.workerId,
                sessionId: session.id,
                sessionGeneration: sessionGenerationRef.current,
            }),
            sendGeneration,
        };
        const isCurrentAttempt = () =>
            attempt.sessionGeneration === sessionGenerationRef.current &&
            attempt.sendGeneration === adminSendGenerationRef.current &&
            attempt.sessionId === renderedSessionIdentityRef.current.id &&
            attempt.workerId === renderedSessionIdentityRef.current.workerId;
        if (!isCurrentAttempt()) {
            setAmbiguousAdminSend(null);
            setAdminMessage('');
            return;
        }
        activeAdminSendRef.current = attempt;
        const { message, clientRequestId } = attempt;
        const optimistic: Message = {
            id: `local:${clientRequestId}`,
            sessionId: String(attempt.sessionId),
            workerId: attempt.workerId,
            message,
            direction: 'OUTGOING' as Message['direction'],
            senderType: 'ADMIN' as Message['senderType'],
            receivedAt: new Date().toISOString(),
            clientRequestId,
            deliveryStatus: 'PENDING',
        };
        if (!ambiguousAdminSend) {
            setMessages((current) => [...current, optimistic]);
        }
        setAdminMessage('');
        setAdminSendPending(true);
        try {
            const receipt = await sendAdminMessageApi(attempt.workerId, { message, clientRequestId });
            if (!isCurrentAttempt()) return;
            if (
                receipt.message &&
                !isAdminSendReceiptMessageForAttempt(attempt, receipt.message)
            ) {
                throw new Error('ADMIN_SEND_RECEIPT_IDENTITY_MISMATCH');
            }
            setMessages((current) => mergeConversationMessage(current, {
                ...(receipt.message ?? optimistic),
                clientRequestId,
                deliveryStatus: 'PENDING',
            }));
            setAdminMessage("");
            setAmbiguousAdminSend(null);
            logger.info(
                `ChatConversation: admin message accepted duplicate=${receipt.duplicate === true}`,
            );
        } catch (error) {
            if (!isCurrentAttempt()) return;
            if (isAmbiguousAdminSendFailure(error)) {
                if (authoritativeAdminRequestIdsRef.current.has(clientRequestId)) {
                    setAmbiguousAdminSend(null);
                    setAdminMessage('');
                } else {
                    setAmbiguousAdminSend(attempt);
                    setAdminMessage(message);
                    setMessages((current) => current.map((item) => messageClientRequestId(item) === clientRequestId
                        ? { ...item, deliveryStatus: 'PENDING' }
                        : item));
                }
            } else {
                setAmbiguousAdminSend(null);
                setAdminMessage(message);
                setMessages((current) => current.map((item) => messageClientRequestId(item) === clientRequestId
                    ? { ...item, deliveryStatus: 'FAILED' }
                    : item));
            }
            logger.error(
                `ChatConversation: admin send failed errorType=${diagnosticErrorType(error)}`,
            );
        } finally {
            if (activeAdminSendRef.current === attempt) {
                activeAdminSendRef.current = null;
                if (isCurrentAttempt()) setAdminSendPending(false);
            }
        }
    };

    // Determine status text
    let statusText = "AI Active";
    let statusColor = "#667781"; // Default color for "AI Active"
    if (isAiPaused) {
        if (isAdminIntervened) {
            statusText = "Admin Mode - AI Paused";
            statusColor = "#d9534f"; // Red for admin intervention
        } else {
            statusText = "AI Paused";
            statusColor = "#f0ad4e"; // Orange for generic paused
        }
    }
    
    if (!session) {
        return <NoChatSelected $isDarkMode={isDarkMode}>Please select a chat to view.</NoChatSelected>;
    }

    const handleOpenWorkerDetails = () => {
        if (session?.workerId) {
            navigate(`/workers/${session.workerId}`);
        }
    };

    const handleToggleTranslation = async () => {
        if (showTranslations) {
            // Just toggle off - no API call needed
            setShowTranslations(false);
            return;
        }

        // Toggle on and translate messages that need translation
        setIsTranslating(true);
        try {
            const messagesToTranslate = messages.filter(
                msg => !msg.translatedMessage && msg.message
            );

            if (messagesToTranslate.length > 0) {
                logger.debug(`Translating ${messagesToTranslate.length} messages to English`);
                const translatedMessages = await Promise.all(
                    messagesToTranslate.map(msg =>
                        translateMessage(msg.id, 'en').catch(err => {
                            logger.warn(
                                `ChatConversation: message translation failed errorType=${diagnosticErrorType(err)}`,
                            );
                            return msg; // Return original on error
                        })
                    )
                );

                // Update messages with translations
                setMessages(prevMessages =>
                    prevMessages.map(msg => {
                        const translated = translatedMessages.find(t => t.id === msg.id);
                        return translated || msg;
                    })
                );
            }
            setShowTranslations(true);
        } catch (error) {
            logger.error(
                `ChatConversation: translation batch failed errorType=${diagnosticErrorType(error)}`,
            );
        } finally {
            setIsTranslating(false);
        }
    };

    return (
        <ConversationContainer $isMobile={isMobile} $embedded={embedded} $isDarkMode={isDarkMode}>
            <Header $isDarkMode={isDarkMode}>
                <HeaderContent>
                    {isMobile && onBack && (
                        <IconButton onClick={onBack} sx={{ marginRight: '10px', color: isDarkMode ? '#F1F5F9' : 'inherit' }}>
                            <ArrowBackIcon />
                        </IconButton>
                    )}
                    <WorkerInfo $isDarkMode={isDarkMode}>
                        {getSessionDisplayName(session)} {/* Display worker name, fallback to phoneNumber or workerId */}
                        <StatusIndicator $isAdminIntervention={isAdminIntervened} $isDarkMode={isDarkMode} style={{ color: statusColor }}>
                            ({statusText})
                        </StatusIndicator>
                    </WorkerInfo>
                </HeaderContent>
                <ActionButtons>
                    <Tooltip
                        title={showTranslations ? "Show original messages" : "Translate to English"}
                        children={(
                            <span>
                                <IconButton
                                    onClick={handleToggleTranslation}
                                    disabled={isTranslating}
                                    sx={{
                                        color: showTranslations ? '#2563EB' : (isDarkMode ? '#F1F5F9' : 'inherit'),
                                        backgroundColor: showTranslations ? (isDarkMode ? 'rgba(37, 99, 235, 0.2)' : 'rgba(37, 99, 235, 0.1)') : 'transparent',
                                    }}
                                >
                                    {isTranslating ? <CircularProgress size={20} /> : <TranslateIcon />}
                                </IconButton>
                            </span>
                        )}
                    />
                    <Button onClick={handleOpenWorkerDetails} title="Open worker detail page">Worker Details</Button>
                    {needsAttribution && (
                        <Button
                            onClick={() => setSuggestDialogOpen(true)}
                            title="Show plausible referral links for this conversation"
                        >
                            Suggest attribution
                        </Button>
                    )}
                    {!isAiPaused ? (
                        <Button disabled={!sessionControlSynchronized} onClick={handlePauseAi} title="Pause AI to send direct messages">Pause AI</Button>
                    ) : (
                        <Button disabled={!sessionControlSynchronized} onClick={handleResumeAi} title="Resume AI conversation">Resume AI</Button>
                    )}
                </ActionButtons>
            </Header>
            <SuggestAttributionDialog
                open={suggestDialogOpen}
                workerId={session.workerId}
                onClose={() => setSuggestDialogOpen(false)}
                // Force the parent session-list query to refresh so the chip
                // flips to "Manual" without a page reload.
                onAttributed={() => {
                    // Custom event the dashboard listens for; falls back to a
                    // refresh hint if no listener attached.
                    window.dispatchEvent(new CustomEvent('session-attribution-changed', { detail: { workerId: session.workerId } }));
                }}
            />
            <TabSwitcher $isDarkMode={isDarkMode}>
                <TabButton $active={activeTab === 'conversation'} $isDarkMode={isDarkMode} onClick={() => setActiveTab('conversation')}>
                    Conversation
                </TabButton>
                <TabButton $active={activeTab === 'debug'} $isDarkMode={isDarkMode} onClick={() => setActiveTab('debug')}>
                    AI Debug
                </TabButton>
            </TabSwitcher>
            {activeTab === 'conversation' ? (
                <>
                    <MessageList>
                        {isLoadingMessages && <p>Loading messages...</p>}
                        {messages.map(msg => (
                            <ChatMessage key={msg.id} message={msg} showTranslation={showTranslations} />
                        ))}
                        {lifecycleEventsEnabled && lifecycleStatus && (
                            <div role="status" aria-live="polite">
                                {lifecycleStatus}
                            </div>
                        )}
                        {lifecycleEventsEnabled && !lifecycleState.synchronized && <div role="status">Synchronizing conversation state</div>}
                        <div ref={messagesEndRef} />
                    </MessageList>
                    {slaExpired && (
                        <ExpiredNotice $isDarkMode={isDarkMode}>
                            ⏳ The 24-hour WhatsApp window has expired. You can't send a direct message
                            until {session.workerFirstName || 'the worker'} replies (message templates aren't supported yet).
                        </ExpiredNotice>
                    )}
                    <MessageInputContainer $isDarkMode={isDarkMode}>
                        <Input
                            type="text"
                            placeholder={
                                slaExpired ? "24h window expired — waiting on the worker to reply."
                                    : !sessionControlSynchronized ? "Synchronizing conversation state..."
                                    : ambiguousAdminSend ? "Delivery confirmation pending"
                                    : isAiPaused ? "Type your admin message..."
                                        : "AI is active. Pause to send admin message."
                            }
                            value={adminMessage}
                            onChange={(e) => setAdminMessage(e.target.value)}
                            disabled={!sessionControlSynchronized || !isAiPaused || slaExpired || Boolean(ambiguousAdminSend)}
                            $isDarkMode={isDarkMode}
                            onKeyPress={(e) => {
                                if (e.key === 'Enter' && isAiPaused && !slaExpired && adminMessage.trim() !== "") {
                                    handleSendAdminMessage();
                                }
                            }}
                        />
                        <SendButton
                            onClick={handleSendAdminMessage}
                            disabled={!sessionControlSynchronized || !isAiPaused || slaExpired || (!ambiguousAdminSend && adminMessage.trim() === "") || adminSendPending}
                            title={
                                slaExpired ? "24h window expired — worker must reply first"
                                    : !sessionControlSynchronized ? "Synchronizing conversation state"
                                    : !isAiPaused ? "Pause AI to send message"
                                        : ambiguousAdminSend ? "Retry pending message"
                                        : "Send admin message"
                            }
                        >
                            {ambiguousAdminSend ? 'Retry' : 'Send'}
                        </SendButton>
                    </MessageInputContainer>
                </>
            ) : (
                <DebugPanel $isDarkMode={isDarkMode}>
                    {isLoadingAiLogs && <p>Loading AI logs...</p>}
                    {!isLoadingAiLogs && aiLogs.length === 0 && <p>No AI traffic recorded yet.</p>}
                    {aiLogs.map(log => {
                        const directionTone: 'in' | 'out' | 'warn' | 'info' =
                            log.direction === 'OUTBOUND_TO_AI'
                                ? 'out'
                                : log.direction === 'INBOUND_FROM_AI'
                                    ? 'in'
                                    : 'warn';
                        const statusTone: 'in' | 'out' | 'warn' | 'info' = log.status === 'INVALID' ? 'warn' : 'info';
                        return (
                            <LogCard key={log.id} $isDarkMode={isDarkMode}>
                                <LogHeader>
                                    <LogBadges>
                                        <Badge $tone={directionTone}>{log.direction}</Badge>
                                        {log.status && <Badge $tone={statusTone}>{log.status}</Badge>}
                                        {log.taskType && <Badge $tone="info">{log.taskType}</Badge>}
                                    </LogBadges>
                                    <div style={{ fontSize: '0.85rem', color: isDarkMode ? '#bbb' : '#444' }}>
                                        {formatTimestamp(log.createdAt)}
                                    </div>
                                </LogHeader>
                                <DebugMeta $isDarkMode={isDarkMode}>
                                    Detailed request and response bodies are hidden to protect candidate data.
                                </DebugMeta>
                            </LogCard>
                        );
                    })}
                </DebugPanel>
            )}
        </ConversationContainer>
    );
};

export default ChatConversation; 
